import { Button } from "@meiden-connect/ui";
import {
  CONNECTOR_CAPS,
  CONNECTOR_CAP_LABELS,
  CONNECTOR_LINE_PATTERNS,
  CONNECTOR_LINE_PATTERN_LABELS,
  CONNECTOR_LINE_TYPES,
  CONNECTOR_LINE_TYPE_LABELS,
  buildConnectorCap,
  type ConnectorCap,
  type ConnectorLinePattern,
  type ConnectorLineType,
} from "@refinement-canvas/domain";
import { connectorPointsAttribute } from "../../../lib/connector-geometry";

// 接続矢印の線の形・線のパターン・両端の形状を選ぶ部品群（選択中の接続矢印のスタイルパネル用）。
// プレビューは実際の描画と同じ共有の形状定義（buildConnectorCap）から描き、選択肢と見た目がずれない
// ようにする。value が null（複数選択で値が割れている）ときは、どの選択肢も強調しない。

/** 端の形状のプレビューの線幅（ボタン上で形を見分けやすい太さ）。 */
const PREVIEW_STROKE_WIDTH = 1.5;
/** プレビューの描画範囲（横長）。 */
const PREVIEW_WIDTH = 36;
const PREVIEW_HEIGHT = 16;

/** どちらの端の形状か。 */
export type ConnectorEnd = "start" | "end";

const END_LABELS: Readonly<Record<ConnectorEnd, string>> = { start: "始点", end: "終点" };

/** 線の形のプレビュー（直線・角丸の折れ線・曲線）。 */
export function ConnectorLineTypePreview({
  lineType,
}: {
  readonly lineType: ConnectorLineType;
}): JSX.Element {
  const d =
    lineType === "straight"
      ? "M 3 13 L 25 3"
      : lineType === "elbowed"
        ? "M 3 13 L 10 13 Q 14 13 14 9 L 14 7 Q 14 3 18 3 L 25 3"
        : "M 3 13 C 14 13 14 3 25 3";
  return (
    <svg aria-hidden="true" width="28" height="16" viewBox="0 0 28 16">
      <path
        d={d}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** 線のパターンのプレビュー（実線・破線）。 */
export function ConnectorLinePatternPreview({
  pattern,
}: {
  readonly pattern: ConnectorLinePattern;
}): JSX.Element {
  const width = 2;
  return (
    <svg aria-hidden="true" width="24" height="12" viewBox="0 0 24 12">
      <line
        x1="2"
        y1="6"
        x2="22"
        y2="6"
        stroke="currentColor"
        strokeWidth={width}
        // 実際の描画と同じ線幅基準の間隔（4w 3w）で見せる。
        strokeDasharray={pattern === "dashed" ? `${width * 4} ${width * 3}` : undefined}
      />
    </svg>
  );
}

/** 端の形状のプレビュー。線の端に、実際の描画と同じ形状を置く。 */
export function ConnectorCapPreview({
  cap,
  end,
}: {
  readonly cap: ConnectorCap;
  readonly end: ConnectorEnd;
}): JSX.Element {
  const y = PREVIEW_HEIGHT / 2;
  const tip = end === "end" ? { x: PREVIEW_WIDTH - 3, y } : { x: 3, y };
  const tail = end === "end" ? { x: 3, y } : { x: PREVIEW_WIDTH - 3, y };
  // 端点へ向かう線の進行方向。
  const direction = { x: end === "end" ? 1 : -1, y: 0 };
  const geometry = buildConnectorCap(cap, tip, direction, PREVIEW_STROKE_WIDTH);
  // 形状の内側へ線を通さない形（中空の丸など）は、実際の描画と同じだけ線を縮める。
  const trimmedTip = geometry
    ? { x: tip.x - direction.x * geometry.trim, y: tip.y }
    : tip;
  return (
    <svg
      aria-hidden="true"
      width={PREVIEW_WIDTH}
      height={PREVIEW_HEIGHT}
      viewBox={`0 0 ${PREVIEW_WIDTH} ${PREVIEW_HEIGHT}`}
      overflow="visible"
    >
      <line
        x1={tail.x}
        y1={tail.y}
        x2={trimmedTip.x}
        y2={trimmedTip.y}
        stroke="currentColor"
        strokeWidth={PREVIEW_STROKE_WIDTH}
        strokeLinecap="round"
      />
      {geometry?.fills.map((polygon, index) => (
        <polygon
          key={`fill-${index}`}
          points={connectorPointsAttribute(polygon)}
          fill="currentColor"
          stroke="currentColor"
          strokeWidth={PREVIEW_STROKE_WIDTH}
          strokeLinejoin="round"
        />
      ))}
      {geometry?.strokes.map((polyline, index) => (
        <polyline
          key={`stroke-${index}`}
          points={connectorPointsAttribute(polyline)}
          fill="none"
          stroke="currentColor"
          strokeWidth={PREVIEW_STROKE_WIDTH}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {geometry?.circles.map((circle, index) => (
        <circle
          key={`circle-${index}`}
          cx={circle.center.x}
          cy={circle.center.y}
          r={circle.radius}
          fill={circle.filled ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth={PREVIEW_STROKE_WIDTH}
        />
      ))}
    </svg>
  );
}

/** 線の形の選択（直線・角丸の折れ線・曲線）。 */
export function ConnectorLineTypeChooser({
  value,
  onChange,
}: {
  readonly value: ConnectorLineType | null;
  readonly onChange: (lineType: ConnectorLineType) => void;
}): JSX.Element {
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      {CONNECTOR_LINE_TYPES.map((lineType) => (
        <Button
          key={lineType}
          type="button"
          variant={value === lineType ? "action emphasis" : "action"}
          className="canvas-toolbar-square-button"
          data-testid={`connector-line-type-${lineType}`}
          aria-label={`線の形: ${CONNECTOR_LINE_TYPE_LABELS[lineType]}`}
          aria-pressed={value === lineType}
          title={CONNECTOR_LINE_TYPE_LABELS[lineType]}
          onClick={() => onChange(lineType)}
        >
          <ConnectorLineTypePreview lineType={lineType} />
        </Button>
      ))}
    </div>
  );
}

/** 線のパターンの選択（実線・破線）。 */
export function ConnectorLinePatternChooser({
  value,
  onChange,
}: {
  readonly value: ConnectorLinePattern | null;
  readonly onChange: (pattern: ConnectorLinePattern) => void;
}): JSX.Element {
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      {CONNECTOR_LINE_PATTERNS.map((pattern) => (
        <Button
          key={pattern}
          type="button"
          variant={value === pattern ? "action emphasis" : "action"}
          className="canvas-toolbar-square-button"
          data-testid={`connector-line-pattern-${pattern}`}
          aria-label={`線の種類: ${CONNECTOR_LINE_PATTERN_LABELS[pattern]}`}
          aria-pressed={value === pattern}
          title={CONNECTOR_LINE_PATTERN_LABELS[pattern]}
          onClick={() => onChange(pattern)}
        >
          <ConnectorLinePatternPreview pattern={pattern} />
        </Button>
      ))}
    </div>
  );
}

/**
 * 経路の直接操作（自動経路に戻す）と、手動経路を配置に合わせて補正して表示していることの表示。
 * 子パネルを開かずに実行できる操作として、親パネルの直接操作の列へ並べる。
 */
export function ConnectorRouteActions({
  hasManualRoute,
  adjusted,
  onResetRoute,
}: {
  /** 選択中のいずれかが手動経路を持つか（持たなければ戻す対象が無いため無効）。 */
  readonly hasManualRoute: boolean;
  /** 手動経路を図形の配置に合わせて補正して表示しているか。 */
  readonly adjusted: boolean;
  readonly onResetRoute: () => void;
}): JSX.Element {
  return (
    <>
      <Button
        type="button"
        variant="action"
        data-testid="connector-reset-route"
        disabled={!hasManualRoute}
        title={
          hasManualRoute
            ? "ドラッグで変えた経路を、自動で決める経路へ戻します"
            : "経路は自動で決まっています"
        }
        onClick={onResetRoute}
      >
        自動経路に戻す
      </Button>
      {adjusted ? (
        <span
          role="status"
          data-testid="connector-route-adjusted"
          style={{ fontSize: "0.85em", whiteSpace: "nowrap" }}
        >
          配置に合わせて経路を補正中
        </span>
      ) : null}
    </>
  );
}

/** 端の形状の選択（12 種。日本語名と選択状態を持つ）。 */
export function ConnectorCapChooser({
  end,
  value,
  onChange,
}: {
  readonly end: ConnectorEnd;
  readonly value: ConnectorCap | null;
  readonly onChange: (cap: ConnectorCap) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label={`${END_LABELS[end]}の形状`}
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(6, var(--canvas-toolbar-square-button-size))",
        gap: "6px",
      }}
    >
      {CONNECTOR_CAPS.map((cap) => (
        <Button
          key={cap}
          type="button"
          variant={value === cap ? "action emphasis" : "action"}
          className="canvas-toolbar-square-button"
          data-testid={`connector-${end}-cap-${cap}`}
          aria-label={`${END_LABELS[end]}: ${CONNECTOR_CAP_LABELS[cap]}`}
          aria-pressed={value === cap}
          title={CONNECTOR_CAP_LABELS[cap]}
          onClick={() => onChange(cap)}
        >
          <ConnectorCapPreview cap={cap} end={end} />
        </Button>
      ))}
    </div>
  );
}
